/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : CommonStepper.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";

const GREEN = "#26890D";
const GREY = "#53565A";

const STEP_SIZE = 18;
const INNER_SIZE = 14;
const CONNECTOR_HEIGHT = 5;

const CommonStepper = ({ steps = [], activeStep = 0 }) => {
  const stepCount = steps.length;
  let stepProgressWidth = `calc((${activeStep} + 0.5) / ${stepCount} * 100%)`;
  if (stepCount <= 1 || activeStep === stepCount - 1) {
    stepProgressWidth = "100%";
  }

  return (
    <Box
      sx={{
        width: "100%",
        p: 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 900,
          display: "grid",
          gridTemplateColumns: `repeat(${stepCount}, 1fr)`,
          alignItems: "center",
          mb: 2,
          position: "relative",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            height: CONNECTOR_HEIGHT,
            bgcolor: GREY,
            borderRadius: CONNECTOR_HEIGHT / 2,
            zIndex: 0,
          }}
        >
          <Box
            sx={{
              height: "100%",
              bgcolor: GREEN,
              borderRadius: CONNECTOR_HEIGHT / 2,
              transition: "width 0.3s ease",
              width: stepProgressWidth,
            }}
          />
        </Box>

        {steps.map((step, idx) => (
          <Box
            key={typeof step === "string" ? step : step?.id || step?.label || idx}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              position: "relative",
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                width: STEP_SIZE,
                height: STEP_SIZE,
                borderRadius: "50%",
                background: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 12px 0 rgba(0,0,0,0.18)",
              }}
            >
              <Box
                sx={{
                  width: INNER_SIZE,
                  height: INNER_SIZE,
                  borderRadius: "50%",
                  background: idx <= activeStep ? GREEN : GREY,
                }}
              />
            </Box>
          </Box>
        ))}
      </Box>
      <Box
        sx={{
          width: "100%",
          maxWidth: 900,
          display: "grid",
          gridTemplateColumns: `repeat(${stepCount}, 1fr)`,
          alignItems: "start",
        }}
      >
        {steps.map((label, idx) => (
          <Typography
            key={typeof label === "string" ? label : label?.id || label?.label || idx}
            sx={{
              fontFamily: "Open Sans",
              fontWeight: 600,
              fontSize: "14px",
              color: "#000",
              textAlign: "center",
              lineHeight: 1.1,
              wordBreak: "break-word",
              whiteSpace: "pre-line",
            }}
          >
            {typeof label === "string" ? label : label?.label || ""}
          </Typography>
        ))}
      </Box>
    </Box>
  );
};

CommonStepper.propTypes = {
  steps: PropTypes.array,
  activeStep: PropTypes.number,
};

export default CommonStepper;
